import { isMobileWeb } from "@repo/shared"

import { ENV_KEYS } from "../constants"
import { AnnouncementItem } from "../servers/newscenter/interface"
import { getClosedAnnouncementId } from "./cookieUtils"
import { isRootPath } from "./pathUtils"
import { getRequestHeaders } from "./requestUtils"

/**
 * 过滤公告数据：移动端只有 www 根路径显示，其他隐藏
 * @description 在服务端判断是否显示公告，避免客户端闪烁
 *
 * 逻辑：
 * - PC端：所有站点都显示
 * - 移动端：只有 www 站点的根路径显示，其他都隐藏
 *
 * @param announcements - 公告列表
 * @param userAgent - User-Agent 字符串
 * @param pathname - 当前路径
 * @param isWww - 是否为 www 站点
 * @returns 过滤后的公告列表
 */
export function filterAnnouncementsByDevice(
  announcements: AnnouncementItem[],
  userAgent: string,
  pathname: string,
  isWww: boolean
): AnnouncementItem[] {
  const isMobile = isMobileWeb(userAgent)

  // PC端：所有站点都显示
  if (!isMobile) {
    return announcements
  }

  // 移动端：只有 www 站点的根路径显示
  if (isWww && isRootPath(pathname)) {
    return announcements
  }

  // 移动端其他情况：隐藏公告
  return []
}

/**
 * 检查是否应该显示公告
 * @param announcements - 公告列表
 * @param currentId - 当前公告 ID（可能是 string 或 number）
 * @param storedId - 存储的已关闭公告 ID
 * @returns 是否应该显示公告
 */
export function shouldShowAnnouncement(
  announcements: AnnouncementItem[],
  currentId: string | number | undefined,
  storedId: string | null
): boolean {
  if (announcements.length === 0) {
    return false
  }

  if (!currentId) {
    return true
  }

  // 统一转换为字符串进行比较
  return String(currentId) !== storedId
}

/**
 * 处理公告数据过滤逻辑
 * @description 基于 Cookie 和设备类型过滤公告数据
 * @param announcements - 公告列表
 * @returns 过滤后的公告列表
 */
export async function filterAnnouncements(announcements: AnnouncementItem[]): Promise<AnnouncementItem[]> {
  if (announcements.length === 0) {
    return []
  }

  // 第一步：基于 Cookie 过滤（检查用户是否已关闭）
  const firstAnnouncement = announcements[0]
  const currentId = firstAnnouncement?.id
  const storedId = await getClosedAnnouncementId()

  const shouldShow = shouldShowAnnouncement(announcements, currentId, storedId)

  if (!shouldShow) {
    return []
  }

  // 第二步：基于设备类型和路径过滤
  const { userAgent, pathname } = await getRequestHeaders()
  const isWww = process.env[ENV_KEYS.APP_NAME] === ENV_KEYS.WWW_APP_NAME

  return filterAnnouncementsByDevice(announcements, userAgent, pathname, isWww)
}
